/**
 * The running tour: which one, and which step. The overlay draws it; this
 * module only moves between steps and records each move.
 */

import { Atom } from '../../state/atom.ts';
import { activeSteps, tourById, type Tour, type TourStep } from './tour-registry.ts';
import { everFinished, recordTour } from './tour-progress.ts';

export interface RunningTour {
  tour: Tour;
  /** The steps that apply, fixed when the tour started. */
  steps: TourStep[];
  index: number;
  /** Arrived here by Back: the step was done already, so it waits for Next. */
  cameBack?: boolean;
}

export const runningTour = new Atom<RunningTour | null>(null);
/** The tours list in the header and account menu. */
export const toursPanelOpen = new Atom<boolean>(false);

function record(run: RunningTour, state: 'started' | 'finished' | 'stopped', step: number): void {
  recordTour(run.tour.id, state, step, run.steps.length, run.tour.version);
}

/** The first tour that must be finished before this one, or null when it is open. */
export function lockedBy(tour: Tour): Tour | null {
  for (const id of tour.requires ?? []) {
    if (!everFinished(id)) return tourById(id) ?? null;
  }
  return null;
}

export function startTour(id: string, from = 0): void {
  const tour = tourById(id);
  if (!tour || lockedBy(tour)) return;
  if (runningTour.value) stopTour();
  toursPanelOpen.set(false);
  tour.prepare?.();
  const steps = activeSteps(tour);
  if (!steps.length) return;
  const run = { tour, steps, index: Math.max(0, Math.min(from, steps.length - 1)) };
  runningTour.set(run);
  record(run, 'started', run.index + 1);
}

/** Move by one step. Past the last step the tour is finished. */
export function moveStep(delta: 1 | -1): void {
  const run = runningTour.value;
  if (!run) return;
  const index = run.index + delta;
  if (index >= run.steps.length) {
    finishTour();
    return;
  }
  const next = { ...run, index: Math.max(0, index), cameBack: delta < 0 };
  runningTour.set(next);
  record(next, 'started', next.index + 1);
}

export function stopTour(): void {
  const run = runningTour.value;
  if (!run) return;
  record(run, 'stopped', run.index + 1);
  runningTour.set(null);
}

export function finishTour(): void {
  const run = runningTour.value;
  if (!run) return;
  record(run, 'finished', run.steps.length);
  runningTour.set(null);
}
